import React, { ReactNode, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthContext } from 'context/auth.context';
import { CaslPermission, hasPermissions } from 'config/ability';
import { LOGIN_LANDING_PAGE } from 'const/const';
import { useSnackbarContext } from 'context/snackbar.context';
import Loading from 'components/Messages/Loading/Loading';
import Layout from 'layout/Layout';
import PermissionCheck from 'components/Abilities/PermissionCheck';

interface PrivateRouteProps {
  permission?: CaslPermission;
  children?: ReactNode;
}

type Props = PrivateRouteProps;

const PrivateRoute = ({ permission, children }: Props): JSX.Element => {
  const { isSessionActive, ability, permissionsLoaded } = useAuthContext();
  const { displaySnackbarMessage } = useSnackbarContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      permissionsLoaded &&
      permission &&
      !hasPermissions(ability, permission.action, permission.subject, permission?.conditions)
    ) {
      navigate(hasPermissions(ability, 'read', 'Campaign') ? LOGIN_LANDING_PAGE : '/');
      displaySnackbarMessage('Invalid Permissions', 'error');
    }
  }, [ability, displaySnackbarMessage, navigate, permission, permissionsLoaded]);

  if (!isSessionActive()) {
    return <Navigate to={'/login'} />;
  }

  if (permissionsLoaded) {
    if (permission) {
      return (
        <Layout>
          <>
            {hasPermissions(
              ability,
              permission.action,
              permission.subject,
              permission?.conditions
            ) && children}
          </>
        </Layout>
      );
    }
    return (
      <Layout>
        <>{children}</>
      </Layout>
    );
  }

  return <Loading />;
};

export default PrivateRoute;
